import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
    DialogContentText,
    Box,
    Grid,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    Typography,
    FormControl,
    InputLabel,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useTranslation } from 'react-i18next';
import { User } from 'models/user';
import { LoadingButton } from '@mui/lab';
import { useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { usePauseReasons } from 'hooks/usePauseReasons';

type PauseResumeConfirmationDialogProps = {
    isPaused: boolean;
    initialUserId?: string;
    open: boolean;
    handleClose: () => void;
    /** `reason` is only set when pausing, and only if one was chosen. */
    handleOk: (userId: string, comment: string, reason?: string) => void;
    isLoading?: boolean;
};

const PauseResumeConfirmationDialog: FC<PauseResumeConfirmationDialogProps> = ({
    isPaused,
    initialUserId,
    open,
    handleClose,
    handleOk,
    isLoading,
}) => {
    const { t: translate } = useTranslation(['common', 'workflow']);
    const currentUser = currentUserVar();
    const { showBoundary } = useDenthubErrorBoundary();

    const [comment, setComment] = useState('');
    const [reason, setReason] = useState('');
    const { pauseReasons } = usePauseReasons({ skip: !open || isPaused });
    // DD-3391: no external tier may read the lab's user list, so asking for it is a
    // hard ACCESS_DENIED. They are the technician on their own work anyway, so the
    // picker is seeded with them rather than left showing a raw id.
    const isExternalUser = permissions.isExternalTechnician(currentUser.role);
    const [users, setUsers] = useState<User[]>(isExternalUser ? [currentUser] : []);
    const [userId, setUserId] = useState(initialUserId ?? currentUser._id);

    useQuery(getTechniciansForUserListTemplate, {
        skip: isExternalUser,
        variables: {
            labId: (currentUser.organization as Organization)?._id,
            filters: { active: true },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => setUsers(technicians),
        onError: (error) => showBoundary(error),
    });

    useEffect(() => {
        if (open) {
            setUserId(initialUserId ?? currentUser._id);
            setComment('');
            setReason('');
        }
    }, [open, initialUserId]);

    const handleConfirm = () => {
        handleOk(userId, comment, !isPaused && reason ? reason : undefined);
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={handleClose}
            title={isPaused ? translate('workflow:UN_PAUSE_STEP') : translate('workflow:PAUSE_STEP')}
            actions={
                <LoadingButton
                    variant="contained"
                    onClick={handleConfirm}
                    autoFocus
                    loading={isLoading || false}
                    data-test="pause-resume-confirmation-dialog.yes-btn"
                >
                    <span>
                        {translate('common:YES')},{' '}
                        {isPaused ? translate('workflow:RESUME') : translate('workflow:PAUSE')}
                    </span>
                </LoadingButton>
            }
        >
            <DialogContentText>
                {isPaused ? translate('workflow:CONFIRM_RESUME') : translate('workflow:CONFIRM_PAUSE')}
            </DialogContentText>
            <Box mt={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2">
                            {isPaused ? translate('workflow:WHO_IS_RESUMING') : translate('workflow:WHO_IS_PAUSING')}
                        </Typography>
                        <Select
                            fullWidth
                            value={userId}
                            onChange={(e: SelectChangeEvent) => setUserId(e.target.value)}
                            renderValue={(value) => {
                                const item = users.find(({ _id }) => _id === value);
                                return item ? `${item.name.firstName} ${item.name.lastName}` : value;
                            }}
                            data-test="pause-resume-confirmation-dialog.user-select"
                        >
                            {users.map((user, index) => (
                                <MenuItem value={user._id} key={index}>
                                    {`${user.name.firstName} ${user.name.lastName}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    {!isPaused && pauseReasons.length > 0 && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="pause-reason-label">{translate('workflow:PAUSE_REASON')}</InputLabel>
                                <Select
                                    labelId="pause-reason-label"
                                    label={translate('workflow:PAUSE_REASON')}
                                    value={reason}
                                    onChange={(e: SelectChangeEvent) => setReason(e.target.value)}
                                    data-test="pause-resume-confirmation-dialog.reason-select"
                                >
                                    <MenuItem value="">
                                        <em>{translate('workflow:NO_PAUSE_REASON')}</em>
                                    </MenuItem>
                                    {pauseReasons.map((pauseReason) => (
                                        <MenuItem value={pauseReason} key={pauseReason}>
                                            {pauseReason}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={comment}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setComment(e.target.value)}
                            label={translate('common:COMMENTS')}
                            data-test="pause-resume-confirmation-dialog.comment-txt"
                        />
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default PauseResumeConfirmationDialog;
